import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PASSWORD_MAX_LEN, PASSWORD_MIN_LEN } from "@kkp/api-client";
import { useQuery } from "@tanstack/react-query";
import { PageContainer } from "@kkp/ui";
import { Button } from "@kkp/ui";
import { Card } from "@kkp/ui";
import { Field, Input } from "../../ui/Input";
import { PasswordInput } from "@kkp/ui";
import { PasswordRules, isPasswordValid } from "@kkp/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import { getProfile, type ClientProfile } from "../../api/profile";
import { changePassword } from "../../api/auth";
import { ApiError } from "../../api/client";
import { toast } from "@kkp/ui";

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Enter your current password."),
    newPassword: z
      .string()
      .min(
        PASSWORD_MIN_LEN,
        `Password must be at least ${PASSWORD_MIN_LEN} characters.`,
      )
      .max(
        PASSWORD_MAX_LEN,
        `Password must be at most ${PASSWORD_MAX_LEN} characters.`,
      )
      .refine(isPasswordValid, "Password does not meet all rules."),
    confirm: z.string().min(1, "Please confirm your new password."),
  })
  .refine((d) => d.newPassword === d.confirm, {
    path: ["confirm"],
    message: "Passwords do not match.",
  });

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

export default function ProfilePage() {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { data, error } = useQuery<ClientProfile, Error>({
    queryKey: ["client-profile"],
    queryFn: getProfile,
    staleTime: 0,
    retry: 1,
  });

  React.useEffect(() => {
    if (error) {
      toast.danger("Failed to load profile.", { title: "Profile" });
    }
  }, [error]);

  const profile = data;

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirm: "" },
  });

  const newPassword = useWatch({ control, name: "newPassword" });
  const confirm = useWatch({ control, name: "confirm" });

  // Mismatch is shown in the rules panel; suppress the duplicate inline error
  // on the Confirm field but keep other errors (e.g. "Please confirm...").
  const confirmFieldError =
    errors.confirm?.message === "Passwords do not match."
      ? undefined
      : errors.confirm?.message;

  const closeDialog = React.useCallback(() => {
    setDialogOpen(false);
    reset();
  }, [reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success("Password updated. Other devices have been signed out.", {
        title: "Password changed",
      });
      closeDialog();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.httpStatus === 401) {
          setError("currentPassword", {
            type: "server",
            message: "Current password is incorrect.",
          });
          return;
        }
        if (err.httpStatus === 400) {
          const message = err.message || "New password is invalid.";
          if (/different from current/i.test(message)) {
            setError("newPassword", {
              type: "server",
              message: "New password must be different from current password.",
            });
          } else {
            setError("newPassword", { type: "server", message });
          }
          return;
        }
      }
      toast.danger("Could not change password. Please try again.", {
        title: "Error",
      });
    }
  });

  return (
    <PageContainer
      title="Profile"
      description="View company and PIC details. Update your password from here."
      width="wide"
      actions={
        <Button variant="primary" onClick={() => setDialogOpen(true)}>
          Change Password
        </Button>
      }
    >
      <div className="grid gap-4">
        <Card>
          <div className="text-sm font-semibold text-white">
            Company Details
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Company Name">
              <Input value={profile?.companyName || ""} readOnly />
            </Field>
            <Field label="Company NPWP">
              <Input value={profile?.companyNpwp || ""} readOnly />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="text-sm font-semibold text-white">PIC Details</div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="PIC Fullname">
              <Input value={profile?.picFullName || ""} readOnly />
            </Field>
            <Field label="PIC Email">
              <Input value={profile?.picEmail || ""} readOnly />
            </Field>
            <Field label="PIC Phonenumber">
              <Input value={profile?.picPhone || ""} readOnly />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="text-sm font-semibold text-white">
            Session Details
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Current IP Address">
              <Input value={profile?.currentIp || ""} readOnly />
            </Field>
            <Field label="Current City">
              <Input value={profile?.currentCity || ""} readOnly />
            </Field>
            <Field label="Current Region">
              <Input value={profile?.currentRegion || ""} readOnly />
            </Field>
            <Field label="Current Country">
              <Input value={profile?.currentCountry || ""} readOnly />
            </Field>
            <Field label="Current User Agent" className="sm:col-span-2">
              <Input value={profile?.currentUserAgent || ""} readOnly />
            </Field>
          </div>
        </Card>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          } else {
            setDialogOpen(true);
          }
        }}
      >
        <DialogContent className="w-full max-w-[760px]">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Update your account password. You&apos;ll be asked for your
              current password to confirm. Other signed-in devices will be
              signed out.
            </DialogDescription>
          </DialogHeader>

          <form className="mt-4 grid gap-4" onSubmit={onSubmit}>
            <Field
              label="Current Password"
              required
              error={errors.currentPassword?.message}
            >
              <PasswordInput
                autoComplete="current-password"
                placeholder="Enter current password"
                {...register("currentPassword")}
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_minmax(0,260px)] md:gap-6">
              <div className="grid gap-4">
                <Field
                  label="New Password"
                  required
                  error={errors.newPassword?.message}
                >
                  <PasswordInput
                    autoComplete="new-password"
                    placeholder="Enter new password"
                    {...register("newPassword")}
                  />
                </Field>
                <Field
                  label="Confirm New Password"
                  required
                  error={confirmFieldError}
                >
                  <PasswordInput
                    autoComplete="new-password"
                    placeholder="Confirm new password"
                    {...register("confirm")}
                  />
                </Field>
              </div>
              <PasswordRules
                password={newPassword}
                confirm={confirm}
                showMatch
                heading="Password requirements"
                className="md:mt-7"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={closeDialog}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
